import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CrowdLogo } from "@/components/CrowdLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Briefcase,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronLeft,
  LayoutGrid
} from "lucide-react";

const navItems = [
  { href: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { href: "/overview", icon: LayoutGrid, label: "Visão Geral" },
  { href: "/clients", icon: Briefcase, label: "Clientes" },
  { href: "/settings", icon: Settings, label: "Configurações" },
];

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const userInitials = user?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "CB";

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar - Crowd SA Navy Style */}
      <aside
        className={cn(
          "hidden lg:flex flex-col bg-[#0A1628] text-white transition-all duration-300",
          collapsed ? "w-16" : "w-64"
        )}
        data-testid="desktop-sidebar"
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <CrowdLogo collapsed={collapsed} className="text-white" />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
            data-testid="collapse-sidebar-btn"
          >
            <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          </Button>
        </div>

        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                    "hover:bg-[#00A7E1]/20 hover:text-[#00A7E1]",
                    isActive 
                      ? "bg-[#00A7E1]/20 text-[#00A7E1] border-l-2 border-[#00A7E1]" 
                      : "text-white/70"
                  )
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span className="sidebar-text">{item.label}</span>}
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-3 px-2 text-white/80 hover:text-white hover:bg-white/10",
                  collapsed && "justify-center px-0"
                )}
                data-testid="user-menu-btn"
              >
                <Avatar className="h-8 w-8 bg-[#00A7E1]">
                  <AvatarFallback className="bg-[#00A7E1] text-[#0A1628] text-xs font-bold">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex flex-col items-start text-left">
                    <span className="text-sm font-medium truncate max-w-[140px]">{user?.name}</span>
                    <span className="text-xs text-white/50 truncate max-w-[140px]">{user?.email}</span>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
                {theme === "light" ? <Moon className="mr-2 h-4 w-4" /> : <Sun className="mr-2 h-4 w-4" />}
                {theme === "light" ? "Modo escuro" : "Modo claro"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#0A1628] border-b border-white/10 z-50 flex items-center justify-between px-4">
        <CrowdLogo className="text-white" />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="text-white hover:bg-white/10"
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "lg:hidden fixed top-14 left-0 bottom-0 w-64 bg-[#0A1628] z-50 transform transition-transform text-white",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="mobile-sidebar"
      >
        <ScrollArea className="h-full py-4">
          <nav className="space-y-1 px-2">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    "hover:bg-[#00A7E1]/20 hover:text-[#00A7E1]",
                    isActive 
                      ? "bg-[#00A7E1]/20 text-[#00A7E1]" 
                      : "text-white/70"
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-8 w-8 bg-[#00A7E1]">
                <AvatarFallback className="bg-[#00A7E1] text-[#0A1628] text-xs font-bold">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-white">{user?.name}</span>
                <span className="text-xs text-white/50">{user?.email}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={toggleTheme} 
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={logout} 
                className="flex-1 border-white/20 text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </ScrollArea>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen lg:min-h-0">
        <div className="lg:hidden h-14" />
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </main>
    </div>
  );
};
